// Comment reactions (feed item [21]).
//
// Reactions are a COMMENT-only feature: posts keep the plain "Mi piace".
// A user has at most ONE reaction per comment — tapping the icon sends the
// default `like`, long-pressing opens the picker. Picking a different reaction
// swaps it; picking the same one again removes it (handled backend-side).
export const REACTION_LIKE = "like";

export const COMMENT_REACTIONS = [
  { type: REACTION_LIKE, emoji: "👍", label: "Mi piace" },
  { type: "love", emoji: "❤️", label: "Adoro" },
  { type: "hug", emoji: "🤗", label: "Abbraccio" },
  { type: "haha", emoji: "😂", label: "Ahah" },
  { type: "sad", emoji: "😢", label: "Triste" },
  { type: "wow", emoji: "😮", label: "Wow" },
];

const EMOJI_BY_TYPE = COMMENT_REACTIONS.reduce((acc, r) => {
  acc[r.type] = r.emoji;
  return acc;
}, {});

const LABEL_BY_TYPE = COMMENT_REACTIONS.reduce((acc, r) => {
  acc[r.type] = r.label;
  return acc;
}, {});

export const getReactionEmoji = (type) => EMOJI_BY_TYPE[type] || null;

export const getReactionLabel = (type) => LABEL_BY_TYPE[type] || "Mi piace";

export const normalizeReactionType = (type, fallback = REACTION_LIKE) =>
  Object.prototype.hasOwnProperty.call(EMOJI_BY_TYPE, type) ? type : fallback;

export const getCurrentPostReaction = (post) => {
  if (!post?.liked) return null;

  const explicitReaction = normalizeReactionType(
    post.my_reaction ??
      post.user_reaction ??
      post.liked_reaction ??
      post.reaction_type,
    null,
  );
  if (explicitReaction) return explicitReaction;

  const aggregateTypes = COMMENT_REACTIONS.map((reaction) => reaction.type).filter(
    (type) => Number(post.reactions?.[type]) > 0,
  );
  // The current API omits the user's reaction type. When every reaction on the
  // post is the same, that aggregate type is still an exact answer.
  if (aggregateTypes.length === 1) return aggregateTypes[0];

  return REACTION_LIKE;
};

/**
 * The likes-list endpoint has returned the reaction in a few different shapes
 * over time. Return null for older records rather than displaying a false type.
 */
export const getLikeItemReactionType = (item) =>
  normalizeReactionType(
    item?.reaction_type ??
      item?.reaction?.type ??
      item?.like?.reaction_type ??
      item?.pivot?.reaction_type,
    null,
  );

/**
 * Apply an add, remove, or swap to the aggregate reaction map without mutating
 * the API object held by React/Redux.
 */
export const transitionReactionCounts = (
  reactions,
  previousReaction,
  nextReaction,
) => {
  const nextCounts =
    reactions && typeof reactions === "object" && !Array.isArray(reactions)
      ? { ...reactions }
      : {};
  if (previousReaction === nextReaction) return nextCounts;

  if (previousReaction) {
    nextCounts[previousReaction] = Math.max(
      0,
      (Number(nextCounts[previousReaction]) || 0) - 1,
    );
  }
  if (nextReaction) {
    nextCounts[nextReaction] =
      (Number(nextCounts[nextReaction]) || 0) + 1;
  }
  return nextCounts;
};

/**
 * Total reactions from the backend's per-type map, e.g. {like: 3, love: 1} -> 4.
 * Falls back to a plain count when the map is missing (older payloads).
 */
export const totalReactions = (reactions, fallback = 0) => {
  if (!reactions || typeof reactions !== "object") return fallback;
  const sum = Object.values(reactions).reduce(
    (acc, n) => acc + (Number(n) || 0),
    0,
  );
  return sum || fallback;
};

/**
 * The most-used distinct reaction types, capped for display. Equal counts use
 * the canonical picker order so the result is stable between renders.
 */
export const topReactionTypes = (reactions, max = 3) => {
  if (!reactions || typeof reactions !== "object") return [];
  const canonicalOrder = new Map(
    COMMENT_REACTIONS.map((reaction, index) => [reaction.type, index]),
  );
  return COMMENT_REACTIONS.map((reaction) => reaction.type)
    .filter((type) => Number(reactions[type]) > 0)
    .sort((a, b) => {
      const countDifference = Number(reactions[b]) - Number(reactions[a]);
      return (
        countDifference ||
        canonicalOrder.get(a) - canonicalOrder.get(b)
      );
    })
    .slice(0, max);
};
